import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../context/DarkModeContext';
import { useRubric } from '../../hooks/useRubric';
import { IoCloseOutline, IoCheckmarkOutline } from 'react-icons/io5';

interface RubricSetupModalProps {
  focusId: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const RubricSetupModal: React.FC<RubricSetupModalProps> = ({ 
  focusId, 
  isOpen, 
  onClose, 
  onComplete 
}) => {
  const { darkMode } = useDarkMode();
  const { templates, createRubric, isCreating } = useRubric();
  const [selectedTemplate, setSelectedTemplate] = useState<string>('standard');

  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const selectedBg = darkMode ? 'bg-gray-700' : 'bg-blue-50';

  const handleSetup = async () => {
    const rubric = await createRubric(focusId, selectedTemplate);
    if (rubric) {
      onComplete();
    }
  };

  if (!isOpen) return null;

  const defaultTemplate = templates?.find(t => t.is_default) || templates?.[0];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className={`relative w-full max-w-2xl ${bgColor} rounded-xl shadow-2xl border ${borderColor} p-6`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-2xl font-semibold ${textColor}`}>
            Set Up Your Evaluation Rubric
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg hover:bg-gray-700 transition-colors ${mutedTextColor}`}
          >
            <IoCloseOutline className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6">
          <p className={`${mutedTextColor}`}>
            To track your progress effectively, we need to set up an evaluation rubric. 
            This will help you understand how your performance will be measured.
          </p>

          {/* Template Selection */}
          {defaultTemplate && (
            <div className={`${cardBg} rounded-lg p-6 border ${borderColor}`}>
              <h3 className={`font-medium ${textColor} mb-4`}>
                Using Standard Template
              </h3>
              
              <div className="space-y-3">
                {defaultTemplate.categories.map((category: any) => (
                  <div key={category.id}>
                    <h4 className={`text-sm font-medium ${textColor} mb-1`}>
                      {category.name} ({Math.round(category.weight * 100)}%)
                    </h4>
                    <p className={`text-xs ${mutedTextColor} mb-2`}>
                      {category.description}
                    </p>
                    <ul className={`text-sm ${mutedTextColor} space-y-1`}>
                      {category.criteria.slice(0, 2).map((criterion: string, idx: number) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-blue-500 mt-0.5">•</span>
                          <span>{criterion}</span>
                        </li>
                      ))}
                      {category.criteria.length > 2 && (
                        <li className={`text-xs ${mutedTextColor} italic ml-4`}>
                          +{category.criteria.length - 2} more criteria
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'} rounded-lg p-4 border ${darkMode ? 'border-blue-800' : 'border-blue-200'}`}>
            <p className={`text-sm ${textColor}`}>
              <strong>Note:</strong> You can customize this rubric later to match your company's specific evaluation criteria.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              darkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSetup}
            disabled={isCreating}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              !isCreating
                ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isCreating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Setting up...
              </>
            ) : (
              <>
                <IoCheckmarkOutline className="w-5 h-5" />
                Set Up Rubric
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default RubricSetupModal;